import React, { useEffect, useState, useMemo } from 'react';
import { AccountDetailsMap, AccountPermissionsMap, AccountBalance, AccountPermissions, Toast } from 'vopay';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import useAccountCreationFailureDialogue from '@/hooks/useDialogues/useAccountCreationFailure.dialogue';
import type { AccountsHandlers, NewAccountData } from './types';
import useFileDownload from '@/hooks/useFiledDownload';

import Routes from './Routes';
import AccountContext, { PartnerAccountContext } from './AccountContext';
import { usePartnerAccountBusinessCase } from '@/providers/PartnerAccountsBusinessCase.provider';
import { useHubSpotData } from '@/providers/HubSpotData.provider';
import { usePartnerAccountMatrix } from '@/providers/PartnerAccountsMatrix.provider';
import { PartnerAccountProvider } from '@/providers/PartnerAccount.provider';
import { usePartnerAccounts } from '../providers/PartnerAccounts.provider';
import { PendingDataProvider, usePendingData } from '@/providers/PendingData.provider';
import { PartnerAccountRequest } from '@/hooks/useApi/usePostPartnerAccount';
import {
  toPartnerAccountStatusFilter,
  type PartnerAccountsRequest,
} from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import { useDocument } from '@/providers/Document.provider';
import { useToast } from '@/providers/Toast.provider';
import { ACCOUNTS_PATH } from '@/components/Entities/paths';

/** Sibling routes under /accounts whose second segment is not an account id. */
const ACCOUNT_ROUTE_RESERVED_SEGMENTS: ReadonlySet<string> = new Set(['create', 'billing', 'signer-create']);

const AccountsContainer = (): React.JSX.Element => {
  /*

    ▼ setup initial state for partner account container ▼

  */
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List' });
  const toast = useToast();
  const { downloadCsvFile } = useFileDownload();
  const { response: fetchPartnerAccountsResponse, refreshPartnerAccounts } = usePartnerAccounts();
  const { response: businessCaseResponse, postBusinessCase } = usePartnerAccountBusinessCase();
  const { response: hubSpotDataResponse, postHubSpotData } = useHubSpotData();
  const { response: accountMatrixResponse, postMatrix } = usePartnerAccountMatrix();
  const { response: documentUploadResponse, postDocument } = useDocument();

  const [details, setDetails] = useState<AccountDetailsMap>({});
  const [permissions, setPermissions] = useState<AccountPermissionsMap>({});
  const [balances, setBalances] = useState<AccountBalance[]>([]);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  // Raised where `details` is written, not where the request resolves, so the two land in the same
  // render: a page gating on it would otherwise see one frame of "settled, and no account".
  const [hasAccountsSettled, setHasAccountsSettled] = useState(false);
  const [uploadDocument, setUploadDocument] = useState(false);
  const [showExportToast, setShowExportToast] = useState(false);
  const { updatePendingData, initPendingData } = usePendingData();

  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // The account pages carry the id in the path; the query form is what the list and the drawers link
  // with. The path stands in when the query has none — a refresh or a shared link carrying only the
  // path, or an embedded list rewriting the query string (the shared Client Account List's filters)
  // — either of which would otherwise leave the page with no account and no tabs.
  const routeAccountId = useMemo(() => {
    const match = matchPath<{ accountId: string }>(location.pathname, {
      path: [`${ACCOUNTS_PATH}/sub-account/:accountId`, `${ACCOUNTS_PATH}/:accountId`],
    });

    const candidate = match?.params.accountId ?? '';

    return ACCOUNT_ROUTE_RESERVED_SEGMENTS.has(candidate) ? '' : candidate;
  }, [location.pathname]);

  const accountId = useMemo(() => queryParams.get('accountId') || routeAccountId, [queryParams, routeAccountId]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const accountStatus = useMemo(() => toPartnerAccountStatusFilter(queryParams.get('status')), [queryParams]);
  const searchText = useMemo(() => queryParams.get('searchText') || '', [queryParams]);

  /*  ▼ account creation state ▼  */

  const [newAccountData, setNewAccountData] = useState<NewAccountData | null>(null);
  const [hasPendingAccountCreationData, setHasPendingAccountCreationData] = useState(false);

  const [isAccountCreationSuccessful, setAccountCreationSuccess] = useState<boolean | null>(null);

  const [, setOnboardingLink] = useState<string | null>(null);
  const [newAccountId, setNewAccountId] = useState('');
  const { response: partnerLoginResponse } = usePartnerLogin();
  const { isInsideSales } = partnerLoginResponse;

  const {
    showDialogue: showAccountsCreationFailureDialogue,
    response: accountCreationResponse,
    postPartnerAccount,
  } = useAccountCreationFailureDialogue();

  /*

    ▼ initial call and handling of account data ▼

  */

  useEffect(() => {
    if (!fetchPartnerAccountsResponse) {
      setBalances([]);

      return;
    }

    if (fetchPartnerAccountsResponse.CSVFile) {
      downloadCsvFile(fetchPartnerAccountsResponse.CSVFile, 'accounts');

      return;
    }

    setDetails(fetchPartnerAccountsResponse.accountDetails);
    setBalances(fetchPartnerAccountsResponse.balances);
    setPermissions(fetchPartnerAccountsResponse.permissions);
    setHasAccountsSettled(true);
  }, [fetchPartnerAccountsResponse]);

  useEffect(() => {
    if (newAccountData) postPartnerAccount(buildPartnerAccountParams(newAccountData));
  }, [newAccountData]);

  useEffect(() => {
    if (accountCreationResponse?.success) {
      setNewAccountId(accountCreationResponse.accountId);
      setHasPendingAccountCreationData(false);
      setAccountCreationSuccess(true);
      setShouldFetchData(true);
    }

    if (accountCreationResponse?.errorMessage) {
      showAccountsCreationFailureDialogue();
      setHasPendingAccountCreationData(false);
      setAccountCreationSuccess(false);
      setNewAccountData(null);
      setShouldFetchData(false);
    }

    if (accountCreationResponse?.onboardingLink) {
      setOnboardingLink(accountCreationResponse.onboardingLink);
    }
  }, [accountCreationResponse]);

  /*

    ▼ handle account business-case data ▼

  */

  useEffect(() => {
    if (isAccountCreationSuccessful && newAccountData) {
      if (isInsideSales) {
        postHubSpotData({
          DealID: newAccountData.hubspotDealId ?? '',
          CompanyWebsite: newAccountData.companyWebsiteURL ?? '',
          PlatformUsage: newAccountData.companyPlatformUsage ?? '',
          SocialMediaPage: newAccountData.socialMediaPage ?? '',
          CompanyPhone: newAccountData.companyPhone ?? '',
        });

        postMatrix({ ...newAccountData, newAccountId });
      } else {
        postBusinessCase({ ...newAccountData, newAccountId });
      }
    }
  }, [isAccountCreationSuccessful]);

  useEffect(() => {
    if (businessCaseResponse || hubSpotDataResponse || documentUploadResponse || accountMatrixResponse)
      setAccountCreationSuccess(null);
  }, [businessCaseResponse, hubSpotDataResponse, documentUploadResponse, accountMatrixResponse]);

  useEffect(() => {
    if (accountMatrixResponse?.success && isInsideSales && newAccountData) {
      setUploadDocument(true);
      postBusinessCase({ ...newAccountData, newAccountId });
    }
  }, [accountMatrixResponse]);

  useEffect(() => {
    if (uploadDocument && newAccountData && fetchPartnerAccountsResponse?.accountDetails[newAccountId]) {
      postDocument(
        {
          DocumentContent: newAccountData.flowOfFundsDiagram ?? '',
          DocumentName: `Flow of Funds - ${
            newAccountData.businessName || `${newAccountData.firstName ?? ''} ${newAccountData.lastName ?? ''}`
          }`,
          DocumentType: 'flowoffunds',
        },
        fetchPartnerAccountsResponse?.accountDetails[newAccountId]?.token ?? '',
      );
    }
  }, [uploadDocument, fetchPartnerAccountsResponse]);

  useEffect(() => {
    if (accountId) {
      setHasAccountsSettled(false);

      // The account pages' tabs wait on this response; without the catch a failure is an unhandled
      // rejection and the page sits under a skeleton with nothing said.
      refreshPartnerAccounts({ VopayAccountID: accountId }).catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        setHasAccountsSettled(true);

        toast.neutral(t('loadError', { defaultValue: 'Could not load your accounts: {{errorMessage}}', errorMessage }));
      });
    }
  }, [accountId]);

  useEffect(() => {
    if (shouldFetchData && accountId) {
      refreshPartnerAccounts({ VopayAccountID: accountId });
      setShouldFetchData(false);
    }
  }, [shouldFetchData, accountId]);

  useEffect(() => {
    if (shouldFetchData && newAccountId) {
      refreshPartnerAccounts({ VopayAccountID: newAccountId });
      setShouldFetchData(false);
    }
  }, [shouldFetchData, newAccountId]);

  const handlers: AccountsHandlers = {
    resetAccountsState: () => {
      setHasPendingAccountCreationData(false);
    },

    initializePermissions: (accountPermissions: AccountPermissions) => {
      initPendingData(accountPermissions);
    },

    onPermissionChange: (permissionId: string, permissionValue: string | boolean) => {
      updatePendingData({ [permissionId]: permissionValue });
    },

    onAccountCreation: (_newAccountData: NewAccountData) => {
      setNewAccountData(_newAccountData);
    },

    exportCSV: (accountName?: string): void => {
      setShowExportToast(true);

      const requestParams: PartnerAccountsRequest = {
        ExportCSV: true,
        Country: country,
        Currency: currency,
        VopayAccountStatus: accountStatus,
        SearchText: searchText,
      };

      if (accountName && accountName.trim() !== '') {
        requestParams.AccountName = accountName;
      }

      refreshPartnerAccounts(requestParams).catch((error) => {
        // The hook logged the cause. The "being prepared" toast has to come down with it, or a
        // failed export reads as one still in progress.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        setShowExportToast(false);

        toast.neutral(
          t('exportError', { defaultValue: 'Could not export the accounts: {{errorMessage}}', errorMessage }),
        );
      });
    },
  };

  const partnerAccountContext: PartnerAccountContext = {
    setHasPendingAccountCreationData,
    hasPendingAccountCreationData,
    setShouldFetchData,
    hasAccountsSettled,
    balances,
    details,
    permissions,
  };

  return (
    <AccountContext.Provider value={partnerAccountContext}>
      <Routes handlers={handlers} />

      <Toast
        isVisible={showExportToast}
        label={t('exportCSVToast')}
        variant="neutral"
        onClose={() => setShowExportToast(false)}
      />
    </AccountContext.Provider>
  );

  function buildPartnerAccountParams(accountData: NewAccountData): PartnerAccountRequest {
    const partnerAccountData: PartnerAccountRequest = {
      accountName: accountData.businessName || accountData.companyBusinessName || '',
      emailAddress: accountData.emailAddress || accountData.businessEmailAddress || '',
      businessName: accountData.companyBusinessName || '',
      fullLegalName: `${accountData.firstName ?? ''} ${accountData.lastName ?? ''}`,
      country: accountData.billingCountry || '',
      packageType: accountData.createBillingPackage && !isInsideSales ? '' : accountData.packageType,
      isClientAccountsEnabled: accountData.isClientAccountsEnabled || false,
      businessType: accountData.businessType || '',
      industry: accountData.industry || '',
      industrySubCategory: accountData.industrySubCategory || '',
      accountingSoftwareIntegrationEnabled: accountData.isIntegrationWithAccountingSoftware || false,
      hubspotDealID: accountData.hubspotDealId,
      hubspotCompanyID: accountData.hubspotCompanyId,
      address: accountData.address || '',
      unit: accountData.unit,
      city: accountData.city || '',
      province: accountData.province || '',
      postalCode: accountData.postalcode || '',
      legalBusinessName: accountData.legalBusinessName || accountData.companyBusinessName || '',
      websiteURL: accountData.websiteURL || accountData.companyWebsiteURL || '',
      businessPhone: accountData.businessPhone || accountData.companyPhone || '',
      legalAgreementNotes: accountData.legalAgreementNotes || '',
      billingPackageData: accountData,
    };

    return partnerAccountData;
  }
};

export default (): React.JSX.Element => (
  <PendingDataProvider>
    <PartnerAccountProvider>
      <AccountsContainer />
    </PartnerAccountProvider>
  </PendingDataProvider>
);
